/**
 * CSV Participant Import — 4-step wizard
 *
 * Client-side CSV parsing, column mapping UI, validation via AJAX, import execution.
 */

var $ = jQuery;

// Module state
var state = {
  csvHeaders: [],
  csvRows: [],
  mapping: {},       // { csvColIndex: fieldKey }
  validatedRows: [],
  selectedRows: [],
  overrides: {},     // { rowIndex: matchedPostId } for duplicate overrides
  fields: null,      // grouped fields from server
  fieldsFlat: null,  // flat fields
  autoMatches: {},
  blankCount: 0,
  existingCount: 0,
  maxPax: 0,         // 0 means no limit (no package items)
  firstRowIsLead: true,
  currentStep: 1
};

// RFC 4180 CSV parser
function parseCsv(text) {
  var rows = [];
  var row = [];
  var field = '';
  var inQuotes = false;
  var i = 0;

  while (i < text.length) {
    var ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < text.length && text[i + 1] === '"') {
          field += '"';
          i += 2;
        } else {
          inQuotes = false;
          i++;
        }
      } else {
        field += ch;
        i++;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
        i++;
      } else if (ch === ',') {
        row.push(field);
        field = '';
        i++;
      } else if (ch === '\r' || ch === '\n') {
        row.push(field);
        field = '';
        if (ch === '\r' && i + 1 < text.length && text[i + 1] === '\n') {
          i++;
        }
        if (row.length > 1 || (row.length === 1 && row[0] !== '')) {
          rows.push(row);
        }
        row = [];
        i++;
      } else {
        field += ch;
        i++;
      }
    }
  }

  // Last field
  row.push(field);
  if (row.length > 1 || (row.length === 1 && row[0] !== '')) {
    rows.push(row);
  }

  return rows;
}

// Generate and download template CSV
function downloadTemplate() {
  if (!state.fields) return;

  var headers = [];
  var examples = [];

  Object.keys(state.fields).forEach(function(category) {
    var group = state.fields[category];
    Object.keys(group).forEach(function(key) {
      var f = group[key];
      headers.push(f.label);

      // Example format hints
      if (f.type === 'date') {
        examples.push('01-Jan-2026');
      } else if (f.type === 'datetime') {
        examples.push('01-Jan-2026 14:30');
      } else if (f.type === 'email') {
        examples.push('name@example.com');
      } else if (f.type === 'select' && f.options) {
        examples.push(f.options.join(' / '));
      } else if (f.type === 'country') {
        examples.push('United Kingdom');
      } else {
        examples.push('');
      }
    });
  });

  var csvContent = headers.map(escapeCsvField).join(',') + '\n' + examples.map(escapeCsvField).join(',') + '\n';
  var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  var url = URL.createObjectURL(blob);
  var link = document.createElement('a');
  link.href = url;
  link.download = 'participant-import-template.csv';
  link.click();
  URL.revokeObjectURL(url);
}

function escapeCsvField(value) {
  if (!value) return '';
  value = String(value);
  if (value.indexOf(',') !== -1 || value.indexOf('"') !== -1 || value.indexOf('\n') !== -1) {
    return '"' + value.replace(/"/g, '""') + '"';
  }
  return value;
}

// Step 1: File upload & preview
function handleFileUpload(file) {
  var reader = new FileReader();
  reader.onload = function(e) {
    var text = e.target.result;
    var parsed = parseCsv(text);

    if (parsed.length < 2) {
      alert('CSV must have at least a header row and one data row.');
      return;
    }

    state.csvHeaders = parsed[0];
    state.csvRows = parsed.slice(1);

    if (state.csvRows.length > 250) {
      alert('Maximum 250 rows allowed. Your file has ' + state.csvRows.length + ' rows.');
      state.csvRows = state.csvRows.slice(0, 250);
    }

    renderStep1Preview();
    $('#csv-btn-next').prop('disabled', false);
  };
  reader.readAsText(file);
}

function renderStep1Preview() {
  $('#csv-file-summary').text('Found ' + state.csvHeaders.length + ' columns, ' + state.csvRows.length + ' rows.');
  $('#csv-file-info').show();

  var $table = $('#csv-preview-table');
  $table.empty();

  // Header row
  var $thead = $('<thead>');
  var $hr = $('<tr>');
  state.csvHeaders.forEach(function(h) {
    $hr.append($('<th>').text(h));
  });
  $thead.append($hr);
  $table.append($thead);

  // First 3 data rows
  var $tbody = $('<tbody>');
  var previewCount = Math.min(3, state.csvRows.length);
  for (var i = 0; i < previewCount; i++) {
    var $dr = $('<tr>');
    state.csvRows[i].forEach(function(cell) {
      $dr.append($('<td>').text(cell));
    });
    $tbody.append($dr);
  }
  $table.append($tbody);
}

// Step 2: Column mapping
function renderStep2() {
  var $tbody = $('#csv-mapping-table tbody');
  $tbody.empty();

  state.csvHeaders.forEach(function(header, index) {
    var $row = $('<tr>');
    $row.append($('<td>').text(header));

    var sampleVal = state.csvRows.length > 0 && state.csvRows[0][index] ? state.csvRows[0][index] : '';
    $row.append($('<td>').append($('<span class="sample-value">').text(sampleVal)));

    var $select = $('<select class="form-control form-control-sm csv-field-select">');
    $select.data('col-index', index);
    $select.append($('<option value="">— Skip —</option>'));

    Object.keys(state.fields).forEach(function(category) {
      var $optgroup = $('<optgroup>').attr('label', category);
      var group = state.fields[category];
      Object.keys(group).forEach(function(key) {
        var f = group[key];
        var label = f.label + (f.required ? ' *' : '');
        $optgroup.append($('<option>').val(key).text(label));
      });
      $select.append($optgroup);
    });

    // Apply auto-match
    if (state.autoMatches[index]) {
      $select.val(state.autoMatches[index]);
    }

    $select.on('change', function() {
      updateMapping();
    });

    $row.append($('<td>').append($select));
    $tbody.append($row);
  });

  updateMapping();
}

function updateMapping() {
  state.mapping = {};
  var usedFields = {};
  var hasDuplicates = false;

  $('#csv-mapping-table .csv-field-select').each(function() {
    var $sel = $(this);
    var colIndex = $sel.data('col-index');
    var fieldKey = $sel.val();

    $sel.removeClass('is-invalid');

    if (fieldKey) {
      if (usedFields[fieldKey]) {
        $sel.addClass('is-invalid');
        hasDuplicates = true;
      } else {
        state.mapping[colIndex] = fieldKey;
        usedFields[fieldKey] = true;
      }
    }
  });

  // Check required fields
  var hasForenames = Object.values(state.mapping).indexOf('core_forenames') !== -1;
  var hasSurname = Object.values(state.mapping).indexOf('core_surname') !== -1;

  var $errors = $('#csv-mapping-errors');
  var msgs = [];
  if (!hasForenames) msgs.push('Forenames must be mapped.');
  if (!hasSurname) msgs.push('Surname must be mapped.');
  if (hasDuplicates) msgs.push('Duplicate field mappings detected.');

  if (msgs.length) {
    $errors.html(msgs.join('<br>')).show();
    $('#csv-btn-next').prop('disabled', true);
  } else {
    $errors.hide();
    $('#csv-btn-next').prop('disabled', false);
  }
}

// Step 3: Server-side validation
function runValidation() {
  $('#csv-validation-loading').show();
  $('#csv-btn-next').prop('disabled', true);

  // Build rows data with mapping
  var rowsData = state.csvRows.map(function(row) {
    var mapped = {};
    Object.keys(state.mapping).forEach(function(colIndex) {
      mapped[colIndex] = row[colIndex] || '';
    });
    return mapped;
  });

  $.post(window.csvImportConfig.ajaxUrl, {
    action: 'csv_import_validate',
    nonce: window.csvImportConfig.nonce,
    enquiry_id: window.csvImportConfig.enquiryId,
    mapping: JSON.stringify(state.mapping),
    rows: JSON.stringify(rowsData),
    first_row_is_lead: state.firstRowIsLead ? '1' : ''
  }, function(response) {
    $('#csv-validation-loading').hide();

    if (!response.success) {
      alert(response.data.message || 'Validation failed.');
      return;
    }

    state.validatedRows = response.data.rows;
    state.blankCount = response.data.blank_count || 0;
    state.selectedRows = [];
    state.overrides = {};

    // Calculate how many new slots are available (blank records don't consume new slots)
    var slotsForNew = 0;
    if (state.maxPax > 0) {
      slotsForNew = Math.max(0, state.maxPax - state.existingCount + state.blankCount);
    }

    // Select all non-error rows by default, respecting pax limit
    var newCount = 0;
    state.validatedRows.forEach(function(row, i) {
      var isDuplicate = row.duplicate && row.duplicate.status === 'duplicate';
      if (row.status !== 'error' && !isDuplicate) {
        // Duplicates with override and blank-filling don't consume new slots
        var wouldBeNew = (i >= state.blankCount); // rows beyond blank count create new records
        if (state.maxPax > 0 && wouldBeNew) {
          newCount++;
          if (newCount > slotsForNew) {
            row._over_limit = true;
            return; // don't auto-select
          }
        }
        state.selectedRows.push(i);
      }
    });

    renderStep3Table();
  }).fail(function() {
    $('#csv-validation-loading').hide();
    alert('Validation request failed. Please try again.');
  });
}

// Count how many selected rows would create NEW records (not updates)
function countNewImports() {
  var newCount = 0;
  var blanksAvailable = state.blankCount;
  state.selectedRows.sort(function(a, b) { return a - b; });
  state.selectedRows.forEach(function(rowIndex) {
    if (state.overrides[rowIndex]) {
      return; // duplicate override — updates existing, no new record
    }
    if (blanksAvailable > 0) {
      blanksAvailable--;
      return; // fills a blank record, no new record
    }
    newCount++;
  });
  return newCount;
}

function updateValidationSummary() {
  var overrideCount = Object.keys(state.overrides).length;
  var summaryParts = [state.selectedRows.length + ' row(s) selected for import'];
  if (state.blankCount > 0) {
    summaryParts.push(state.blankCount + ' existing blank record(s) will be updated first');
  }
  if (overrideCount > 0) {
    summaryParts.push(overrideCount + ' existing record(s) will be overwritten');
  }

  var newCount = countNewImports();
  if (state.maxPax > 0) {
    var totalAfter = state.existingCount + newCount;
    summaryParts.push(newCount + ' new record(s) will be created (total: ' + totalAfter + '/' + state.maxPax + ' pax)');
  }

  var errorCount = 0;
  var overLimitCount = 0;
  state.validatedRows.forEach(function(r) {
    var dup = r.duplicate || {};
    if (r.status === 'error' && dup.status !== 'duplicate') errorCount++;
    if (r._over_limit) overLimitCount++;
  });
  if (errorCount) summaryParts.push(errorCount + ' row(s) with errors');
  if (overLimitCount) summaryParts.push(overLimitCount + ' row(s) over pax limit');

  $('#csv-validation-summary').html(summaryParts.join('. ') + '.').show();
}

function renderStep3Table() {
  var $thead = $('#csv-validation-table thead');
  var $tbody = $('#csv-validation-table tbody');
  $thead.empty();
  $tbody.empty();

  // Build header
  var $hr = $('<tr>');
  $hr.append($('<th>').html('<input type="checkbox" id="csv-select-all" checked>'));
  $hr.append($('<th>').text('#'));

  var mappedFieldKeys = [];
  Object.keys(state.mapping).forEach(function(colIndex) {
    var fieldKey = state.mapping[colIndex];
    mappedFieldKeys.push(fieldKey);
    var label = state.fieldsFlat[fieldKey] ? state.fieldsFlat[fieldKey].label : fieldKey;
    $hr.append($('<th>').text(label));
  });
  $hr.append($('<th>').text('Status'));
  $thead.append($hr);

  // Build rows
  state.validatedRows.forEach(function(rowResult, rowIndex) {
    var $tr = $('<tr>');
    var isSelected = state.selectedRows.indexOf(rowIndex) !== -1;
    var isError = rowResult.status === 'error';
    var dup = rowResult.duplicate || {};
    var isDuplicate = dup.status === 'duplicate';
    var isNameMatch = dup.status === 'warning';
    var isLeadUpdate = dup.status === 'lead_update';

    // Duplicate rows are selectable (unchecked by default), only field-validation errors are disabled
    var isDisabled = isError && !isDuplicate;

    $tr.append($('<td>').html('<input type="checkbox" class="csv-row-check" data-row="' + rowIndex + '"' + (isSelected ? ' checked' : '') + (isDisabled ? ' disabled' : '') + (isDuplicate ? ' data-duplicate-id="' + dup.matched_id + '"' : '') + '>'));
    $tr.append($('<td>').text(rowIndex + 1));

    mappedFieldKeys.forEach(function(fieldKey) {
      var cell = rowResult.cells[fieldKey];
      var $td = $('<td>');

      if (cell) {
        if (cell.display) {
          $td.html('<span class="csv-original">' + $('<span>').text(cell.value).html() + '</span> <span class="csv-arrow">&rarr;</span> <span class="csv-interpreted">' + $('<span>').text(cell.display).html() + '</span>');
        } else {
          $td.text(cell.value);
        }
        $td.addClass('cell-' + cell.status);
        if (cell.message) {
          $td.attr('title', cell.message);
        }
        // Make error/warning cells editable
        if (cell.status === 'error' || cell.status === 'warning') {
          $td.addClass('csv-cell-editable');
          $td.data('row-index', rowIndex);
          $td.data('field-key', fieldKey);
          $td.data('original-value', cell.value);
        }
      }

      $tr.append($td);
    });

    // Status column
    var $statusTd = $('<td>');
    var isOverLimit = rowResult._over_limit;
    if (isError && !isDuplicate) {
      $statusTd.html('<span class="text-danger">Error</span>');
    } else if (isOverLimit) {
      $statusTd.html('<span class="text-danger">Over pax limit</span>');
    } else if (isLeadUpdate) {
      $statusTd.html('<span class="text-info">Lead — updates Participant 1</span>');
    } else if (isDuplicate) {
      $statusTd.html('<span class="text-warning">Duplicate of: ' + (dup.matched_name || 'Unknown') + '</span><br><small class="text-muted csv-dup-hint">Check to update existing record</small>');
    } else if (isNameMatch) {
      $statusTd.html('<span class="text-warning">Possible duplicate of: ' + (dup.matched_name || 'Unknown') + '</span>');
    } else {
      $statusTd.html('<span class="text-success">OK</span>');
    }
    $tr.append($statusTd);

    $tbody.append($tr);
  });

  updateValidationSummary();

  // Enable import button
  $('#csv-btn-import').toggle(state.selectedRows.length > 0);

  // Event handlers
  $('#csv-select-all').on('change', function() {
    var checked = $(this).is(':checked');
    $('.csv-row-check:not(:disabled)').prop('checked', checked).trigger('change');
  });

  $('.csv-row-check').on('change', function() {
    var $cb = $(this);
    var rowIndex = $cb.data('row');
    var dupId = $cb.data('duplicate-id');
    var idx = state.selectedRows.indexOf(rowIndex);

    if ($cb.is(':checked') && idx === -1) {
      // Enforce pax limit: count how many new records would be created
      if (state.maxPax > 0 && !dupId) {
        var wouldCreate = countNewImports() + 1;
        var slotsForNew = Math.max(0, state.maxPax - state.existingCount + state.blankCount);
        if (wouldCreate > slotsForNew) {
          $cb.prop('checked', false);
          alert('Cannot add more participants. Pax limit (' + state.maxPax + ') would be exceeded.');
          return;
        }
      }
      state.selectedRows.push(rowIndex);
      if (dupId) {
        state.overrides[rowIndex] = dupId;
        $cb.closest('tr').find('.csv-dup-hint').text('Will update existing record');
      }
    } else if (!$cb.is(':checked') && idx !== -1) {
      state.selectedRows.splice(idx, 1);
      if (dupId) {
        delete state.overrides[rowIndex];
        $cb.closest('tr').find('.csv-dup-hint').text('Check to update existing record');
      }
    }

    $('#csv-btn-import').toggle(state.selectedRows.length > 0);
    updateValidationSummary();
  });

  // Inline editing for error/warning cells
  $('#csv-validation-table').on('click', '.csv-cell-editable', function() {
    var $td = $(this);
    if ($td.find('input').length) return; // already editing

    var rowIndex = $td.data('row-index');
    var fieldKey = $td.data('field-key');
    var currentValue = $td.data('original-value') || '';

    var $input = $('<input type="text" class="form-control form-control-sm csv-inline-edit">')
      .val(currentValue)
      .css('width', '100%');

    $td.empty().append($input);
    $input.focus().select();

    function saveEdit() {
      var newValue = $input.val().trim();
      if (newValue === currentValue) {
        // No change — restore original display
        revalidateRow(rowIndex);
        return;
      }

      // Find csv column index for this field
      var colIndex = null;
      Object.keys(state.mapping).forEach(function(ci) {
        if (state.mapping[ci] === fieldKey) colIndex = ci;
      });

      if (colIndex !== null) {
        // Update source data
        state.csvRows[rowIndex][colIndex] = newValue;
      }

      // Re-validate this row
      revalidateRow(rowIndex);
    }

    $input.on('keydown', function(e) {
      if (e.which === 13) { // Enter
        e.preventDefault();
        saveEdit();
      } else if (e.which === 27) { // Escape
        revalidateRow(rowIndex);
      }
    });
    $input.on('blur', saveEdit);
  });
}

// Re-validate a single row and update its display in the table
function revalidateRow(rowIndex) {
  var rowData = {};
  Object.keys(state.mapping).forEach(function(colIndex) {
    rowData[colIndex] = state.csvRows[rowIndex][colIndex] || '';
  });

  $.post(window.csvImportConfig.ajaxUrl, {
    action: 'csv_import_validate',
    nonce: window.csvImportConfig.nonce,
    enquiry_id: window.csvImportConfig.enquiryId,
    mapping: JSON.stringify(state.mapping),
    rows: JSON.stringify([rowData])
  }, function(response) {
    if (!response.success || !response.data.rows || !response.data.rows[0]) return;

    var newResult = response.data.rows[0];
    state.validatedRows[rowIndex] = newResult;

    // Update selection: if row was error and is now OK, auto-select it
    var wasSelected = state.selectedRows.indexOf(rowIndex) !== -1;
    var isDuplicate = newResult.duplicate && newResult.duplicate.status === 'duplicate';
    if (newResult.status !== 'error' && !isDuplicate && !wasSelected) {
      state.selectedRows.push(rowIndex);
    } else if (newResult.status === 'error' && !isDuplicate && wasSelected) {
      state.selectedRows.splice(state.selectedRows.indexOf(rowIndex), 1);
    }

    // Re-render the specific row in the table
    var $tbody = $('#csv-validation-table tbody');
    var $row = $tbody.find('tr').eq(rowIndex);
    if ($row.length) {
      // Rebuild the row content
      var mappedFieldKeys = [];
      Object.keys(state.mapping).forEach(function(ci) {
        mappedFieldKeys.push(state.mapping[ci]);
      });

      var isSelected = state.selectedRows.indexOf(rowIndex) !== -1;
      var isError = newResult.status === 'error';
      var dup = newResult.duplicate || {};
      var isDup = dup.status === 'duplicate';
      var isNameMatch = dup.status === 'warning';
      var isDisabled = isError && !isDup;

      $row.empty();
      $row.append($('<td>').html('<input type="checkbox" class="csv-row-check" data-row="' + rowIndex + '"' + (isSelected ? ' checked' : '') + (isDisabled ? ' disabled' : '') + (isDup ? ' data-duplicate-id="' + dup.matched_id + '"' : '') + '>'));
      $row.append($('<td>').text(rowIndex + 1));

      mappedFieldKeys.forEach(function(fieldKey) {
        var cell = newResult.cells[fieldKey];
        var $td = $('<td>');
        if (cell) {
          if (cell.display) {
            $td.html('<span class="csv-original">' + $('<span>').text(cell.value).html() + '</span> <span class="csv-arrow">&rarr;</span> <span class="csv-interpreted">' + $('<span>').text(cell.display).html() + '</span>');
          } else {
            $td.text(cell.value);
          }
          $td.addClass('cell-' + cell.status);
          if (cell.message) {
            $td.attr('title', cell.message);
          }
          if (cell.status === 'error' || cell.status === 'warning') {
            $td.addClass('csv-cell-editable');
            $td.data('row-index', rowIndex);
            $td.data('field-key', fieldKey);
            $td.data('original-value', cell.value);
          }
        }
        $row.append($td);
      });

      // Status column
      var $statusTd = $('<td>');
      if (isError && !isDup) {
        $statusTd.html('<span class="text-danger">Error</span>');
      } else if (isDup) {
        $statusTd.html('<span class="text-warning">Duplicate of: ' + (dup.matched_name || 'Unknown') + '</span><br><small class="text-muted csv-dup-hint">Check to update existing record</small>');
      } else if (isNameMatch) {
        $statusTd.html('<span class="text-warning">Possible duplicate of: ' + (dup.matched_name || 'Unknown') + '</span>');
      } else {
        $statusTd.html('<span class="text-success">OK</span>');
      }
      $row.append($statusTd);
    }

    updateValidationSummary();
    $('#csv-btn-import').toggle(state.selectedRows.length > 0);
  });
}

// Step 4: Import execution
function runImport() {
  $('#csv-btn-import').hide();
  $('#csv-btn-back').hide();
  $('#csv-import-progress').show();
  $('#csv-import-results').hide();

  var selectedData = [];
  var overridesMap = {};
  state.selectedRows.sort(function(a, b) { return a - b; });
  state.selectedRows.forEach(function(rowIndex, newIndex) {
    selectedData.push(state.csvRows[rowIndex]);
    // Re-map override indices to the new sequential array
    if (state.overrides[rowIndex]) {
      overridesMap[newIndex] = state.overrides[rowIndex];
    }
  });

  // Build mapped rows
  var mappedRows = selectedData.map(function(row) {
    var mapped = {};
    Object.keys(state.mapping).forEach(function(colIndex) {
      mapped[colIndex] = row[colIndex] || '';
    });
    return mapped;
  });

  $.post(window.csvImportConfig.ajaxUrl, {
    action: 'csv_import_execute',
    nonce: window.csvImportConfig.nonce,
    enquiry_id: window.csvImportConfig.enquiryId,
    mapping: JSON.stringify(state.mapping),
    rows: JSON.stringify(mappedRows),
    overrides: JSON.stringify(overridesMap),
    first_row_is_lead: state.firstRowIsLead ? '1' : ''
  }, function(response) {
    $('#csv-import-progress').hide();
    $('#csv-import-results').show();

    if (!response.success) {
      $('#csv-import-results-summary')
        .removeClass('alert-success alert-warning')
        .addClass('alert-danger')
        .text(response.data.message || 'Import failed.');
      return;
    }

    var data = response.data;
    var alertClass = data.failed > 0 ? 'alert-warning' : 'alert-success';
    $('#csv-import-results-summary')
      .removeClass('alert-success alert-warning alert-danger')
      .addClass(alertClass)
      .text(data.updated + ' updated, ' + data.created + ' created, ' + data.failed + ' failed.');

    // Show per-row details if any failures
    if (data.details && data.details.length) {
      var $details = $('#csv-import-results-details');
      $details.empty();
      var hasFailures = false;
      data.details.forEach(function(d, i) {
        if (!d.success) {
          hasFailures = true;
          $details.append($('<p class="text-danger mb-1">').text('Row ' + (i + 1) + ': ' + d.message));
        }
      });
      if (!hasFailures) {
        $details.hide();
      }
    }

    // Update close button to reload
    $('#csv-btn-close').off('click').on('click', function() {
      location.reload();
    });
  }).fail(function() {
    $('#csv-import-progress').hide();
    $('#csv-import-results').show();
    $('#csv-import-results-summary')
      .removeClass('alert-success alert-warning')
      .addClass('alert-danger')
      .text('Import request failed. Please try again.');
  });
}

// Progress polling (simulated via single request for now)
function updateProgressBar(percent) {
  $('#csv-import-progress-bar').css('width', percent + '%').text(Math.round(percent) + '%');
}

// Step navigation
function goToStep(step) {
  state.currentStep = step;

  $('.csv-import-step').hide();
  $('#csv-step-' + step).show();

  // Update step indicators
  $('.csv-step').removeClass('active completed');
  $('.csv-step').each(function() {
    var s = parseInt($(this).data('step'), 10);
    if (s < step) $(this).addClass('completed');
    if (s === step) $(this).addClass('active');
  });

  // Button visibility
  $('#csv-btn-back').toggle(step === 2 || step === 3);
  $('#csv-btn-next').toggle(step === 1 || step === 2).prop('disabled', false);
  $('#csv-btn-import').toggle(step === 3 && state.selectedRows.length > 0);

  if (step === 4) {
    $('#csv-btn-next').hide();
    $('#csv-btn-back').hide();
  }
}

function initCsvImport() {
  // Fetch fields from server
  $.post(window.csvImportConfig.ajaxUrl, {
    action: 'csv_import_get_fields',
    nonce: window.csvImportConfig.nonce
  }, function(response) {
    if (response.success) {
      state.fields = response.data.fields;
      state.fieldsFlat = {};
      Object.keys(state.fields).forEach(function(cat) {
        var group = state.fields[cat];
        Object.keys(group).forEach(function(key) {
          state.fieldsFlat[key] = group[key];
        });
      });
    }
  });

  // File input
  $('#csv-file-input').on('change', function() {
    var file = this.files[0];
    if (file) {
      handleFileUpload(file);
    }
  });

  // Download template
  $('#csv-download-template').on('click', function(e) {
    e.preventDefault();
    downloadTemplate();
  });

  // Next button
  $('#csv-btn-next').on('click', function() {
    if (state.currentStep === 1) {
      if (state.csvHeaders.length === 0) return;

      // Get auto-matches from server
      $.post(window.csvImportConfig.ajaxUrl, {
        action: 'csv_import_get_fields',
        nonce: window.csvImportConfig.nonce,
        headers: JSON.stringify(state.csvHeaders),
        enquiry_id: window.csvImportConfig.enquiryId
      }, function(response) {
        if (response.success) {
          state.fields = response.data.fields;
          state.autoMatches = response.data.auto_matches || {};
          state.blankCount = response.data.blank_count || 0;
          state.existingCount = response.data.existing_count || 0;
          state.maxPax = response.data.max_pax || 0;

          state.fieldsFlat = {};
          Object.keys(state.fields).forEach(function(cat) {
            var group = state.fields[cat];
            Object.keys(group).forEach(function(key) {
              state.fieldsFlat[key] = group[key];
            });
          });

          var infoParts = [];
          if (state.blankCount > 0) {
            infoParts.push('This enquiry has ' + state.blankCount + ' blank participant record(s). CSV data will be imported into these records first.');
          }
          if (state.maxPax > 0) {
            var slotsAvailable = Math.max(0, state.maxPax - state.existingCount + state.blankCount);
            infoParts.push('Pax limit: ' + state.maxPax + '. ' + slotsAvailable + ' new import slot(s) available.');
            if (state.csvRows.length > slotsAvailable) {
              infoParts.push('Your CSV has ' + state.csvRows.length + ' rows — only the first ' + slotsAvailable + ' will be selectable.');
            }
          }
          if (infoParts.length) {
            $('#csv-blank-info').html(infoParts.join('<br>')).show();
          }

          goToStep(2);
          renderStep2();
        }
      });
    } else if (state.currentStep === 2) {
      state.firstRowIsLead = $('#csv-first-row-lead').is(':checked');
      goToStep(3);
      runValidation();
    }
  });

  // Back button
  $('#csv-btn-back').on('click', function() {
    if (state.currentStep > 1) {
      goToStep(state.currentStep - 1);
      if (state.currentStep === 2) {
        renderStep2();
      }
    }
  });

  // Import button
  $('#csv-btn-import').on('click', function() {
    if (!confirm('Import ' + state.selectedRows.length + ' participant(s)? This cannot be undone.')) return;
    goToStep(4);
    updateProgressBar(10);
    runImport();
  });

  // Send invites button
  // Modal reset on close
  $('#csv-import-modal').on('hidden.bs.modal', function() {
    // If import was completed, reload
    if (state.currentStep === 4 && $('#csv-import-results').is(':visible')) {
      location.reload();
      return;
    }

    // Reset state
    state.csvHeaders = [];
    state.csvRows = [];
    state.mapping = {};
    state.validatedRows = [];
    state.selectedRows = [];
    state.overrides = {};
    state.autoMatches = {};
    state.blankCount = 0;
    state.existingCount = 0;
    state.maxPax = 0;
    state.firstRowIsLead = true;
    state.currentStep = 1;

    $('#csv-file-input').val('');
    $('#csv-file-info').hide();
    $('#csv-blank-info').hide();
    $('#csv-btn-next').prop('disabled', true);
    goToStep(1);
  });
}

export default function() {
  if ($('#csv-import-modal').length) {
    initCsvImport();
  }
}
